import React, { useContext, useState } from "react";
import { IDefaultHoursDay, IProject, IProjectResults, ITimeEntry, IUser } from "../../types/types";
import { Spinner } from "@fluentui/react-components";
import DescriptionDialog from "../AddDescriptionDialog";
import * as Constants from "../../utils/constants";
import { cloneDeep } from "lodash";
import { ApiContext } from "../../providers/ApiProvider";
import { getFinnishHoliday } from "../../utils/finnishHolidays";

interface IWeektableDateCell {
    date: number;
    placeholder: string;
    projectID: string;
    fieldName: string;
    project: IProjectResults;
    selectedDays: number[];
    loadingCells: { [key: string]: boolean };
    jwtoken: string;
    field: { [key: string]: string };
    locked: boolean;
    dbHours: ITimeEntry[];
    user: IUser;
    projectsData: IProjectResults[] | undefined;
    setField: (field: { [key: string]: string }) => void;
    setLoadingCells: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
    setProjectsData: (projectsData: IProjectResults[] | undefined) => void;
    fetchTimeEntries: (week: any[]) => Promise<void>;
    focusedCellsRef: React.MutableRefObject<{ [key: string]: boolean }>;
    fieldRef: React.MutableRefObject<{ [key: string]: string } | undefined>;
    changedCells: { [key: string]: boolean };
    localChangesRef: React.MutableRefObject<{ [key: string]: string }>;
    // True when this cell's project is the default project for this specific day
    isDefaultForDay?: boolean;
    isManualDefaultDay?: boolean;
    onDefaultHoursDayUpdated?: (day: IDefaultHoursDay) => void;
}

export default function WeektableDateCell(props: IWeektableDateCell) {
    const { submitTimeEntry, updateTimeEntry, deleteTimeEntry, updateDescription, updateProjectSettings, resetDefaultHoursForTimeEntry } =
        useContext(ApiContext);

    const [prev, setPrev] = useState<string | number | null>(null);
    const [isFocused, setIsFocused] = useState<boolean>(false);

    const cellKey = `${props.projectID}-${props.date.valueOf()}`;
    const isLoading = props.loadingCells[cellKey];
    const isChanged = props.changedCells[cellKey];
    const isDefaultForDay = !!props.isDefaultForDay;

    /**
     * Get project index from dbHours array if dbhours has project with given id and from date is same as given(current cell date)
     * @param projectID project id
     * @param date curren cell date
     * @returns project index from dbHours array or -1 if not found
     */
    const getProjectIndex = (projectID: any, date: { valueOf: any }) => {
        let ret: any = -1;
        ret = props.dbHours.findIndex((obj: ITimeEntry) => {
            return obj.project_id === projectID && obj.from === date.valueOf();
        });
        return ret;
    };

    /**
     * Check if projects object has project with given id and from date is same as given(current cell date)
     * @param projectID project id
     * @param date current cell date
     * @returns boolean true if project is found and false if not
     */
    const getProjectObject = (projectID: any, date: { valueOf: () => any }) => {
        let ret: boolean = false;
        ret = props.dbHours.some((project) => {
            return project.project_id === projectID && project.from === date.valueOf();
        });
        return ret;
    };

    /**
     * @param projectID project id
     * @param date current cell date
     * @param onlyID if true returns only id of time entry
     * @returns either fulls string (projectID + date) or timeEntry database id or undefined if project is not found
     */
    const handleID = (projectID: any, date: { valueOf: () => any }, onlyID: boolean) => {
        let id: string | undefined = undefined;

        if (getProjectObject(projectID, date)) {
            const idx = getProjectIndex(projectID, date);
            if (onlyID) {
                id = props.dbHours[idx].id; //timeEntry database id
            } else {
                id = `${projectID}-${date.valueOf()}`; //Full string (projectid + date)
            }
        }
        return id;
    };

    const getDescription = (projectID: string, date: { valueOf: any }) => {
        let description = null;
        props.dbHours.some((entry) => {
            if (entry.project_id === projectID && entry.from === date.valueOf() && entry.description !== "") {
                description = entry.description;
                return null;
            }
            return null;
        });

        return description;
    };

    const id = handleID(props.projectID, props.date, false); // Full string (projectid + date) or undefined if project is not found
    const timeEntryID = handleID(props.projectID, props.date, true); // timeEntry database id for Description dialog
    const hasDescription = getDescription(props.projectID, props.date);
    const isLocked = props.locked;
    const isDefaultProject = props.projectID === Constants.DEFAULT_PROJECT_ID;
    // Treat both the legacy DEFAULT_PROJECT_ID and any per-day default project (admin-assigned) as "default hours" projects
    const isDefaultHoursProjectForDay = isDefaultProject || isDefaultForDay;
    const isDisabled = isLocked && !isDefaultHoursProjectForDay; // Default Hours should never be disabled
    const holiday = getFinnishHoliday(new Date(props.date));

    // submit new time entry
    // Note: We don't manually refresh here - the realtime WebSocket will handle the refresh
    const _submitTimeEntry = async (data: ITimeEntry) => {
        await submitTimeEntry(props.jwtoken, data);
        // Realtime WebSocket will trigger refresh automatically
    };

    //Update existing time entry
    // Note: We don't manually refresh here - the realtime WebSocket will handle the refresh
    const _updateTimeEntry = async (data: ITimeEntry, id: any) => {
        await updateTimeEntry(props.jwtoken, data, id);
        // Realtime WebSocket will trigger refresh automatically
    };

    const _deleteTimeEntry = async (projectID: any, from: number) => {
        await deleteTimeEntry(props.jwtoken, projectID, from);
        // Realtime WebSocket will trigger refresh automatically
    };

    const _handleDescriptionReady = async (body: { description: string }, cellKey: string, id: string) => {
        try {
            props.setLoadingCells((prev: { [key: string]: boolean }) => ({
                ...prev,
                [cellKey]: true,
            }));
            await updateDescription(props.jwtoken, body, id);
            // Realtime WebSocket will trigger refresh automatically
        } catch (error) {
            console.error(error);
        } finally {
            props.setLoadingCells((prev: { [key: string]: boolean }) => ({
                ...prev,
                [cellKey]: false,
            }));
        }
    };

    const handleOnChange = (data: { value: any }) => {
        let current = data.value.replace(",", ".");
        if (current > Constants.MAXHOURS) current = Constants.MAXHOURS;
        if (current < Constants.MINHOURS) current = Constants.MINHOURS;
        return current;
    };

    // Format numeric value to always show one decimal place
    const formatValue = (value: string | undefined): string => {
        if (!value || value === "" || value === "0") return "";
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && numValue !== 0) {
            return numValue.toFixed(1);
        }
        return value;
    };

    const handleOnBlur = async (
        target: EventTarget & HTMLInputElement,
        prev: string | number | null,
        projectID: any,
        date: { valueOf: () => any },
        project: IProjectResults,
    ) => {
        const cellKey = `${projectID}-${date.valueOf()}`;
        props.setLoadingCells((prev: { [key: string]: boolean }) => ({
            ...prev,
            [cellKey]: true,
        }));

        try {
            let current: string | number = target.value as unknown as number;
            if (isNaN(current)) current = "0";
            const from: number = Number(date.valueOf());
            const to: number = from + 3600 * 1000 * parseFloat(current as string);
            const today = new Date();

            if (current !== prev) {
                if (current == 0) {
                    // Skip if prev was empty
                    if (prev !== "" && prev !== "0") {
                        //delete time entry from database
                        console.info("Delete time entry");
                        await _deleteTimeEntry(projectID, from);
                        // Clear local change tracking after successful delete
                        delete props.localChangesRef.current[props.fieldName];
                    } else {
                        // Clear the input field (show nothing in it)
                        const updatedField = { ...props.field };
                        updatedField[props.fieldName] = "";
                        props.setField(updatedField);
                        props.setLoadingCells((prev: { [key: string]: boolean }) => ({ ...prev, [cellKey]: false }));
                        // Clear local change tracking
                        delete props.localChangesRef.current[props.fieldName];
                        return;
                    }
                } else {
                    // We use id field to tell that we have already timeEntry in database for this field
                    const idx = getProjectIndex(projectID, date);
                    if (idx === -1) {
                        // Handle new time entry
                        const newEntry: ITimeEntry = {
                            project_id: projectID,
                            created_by: props.user?.id || "",
                            created: today.valueOf(),
                            modified: today.valueOf(),
                            description: "",
                            from: from,
                            to: to,
                            approved: false,
                        };
                        await _submitTimeEntry(newEntry);
                        // Clear local change tracking after successful save
                        delete props.localChangesRef.current[props.fieldName];
                    } else {
                        // Handle update time entry
                        const existingEntry = props.dbHours[idx];
                        existingEntry.from = from;
                        existingEntry.to = to;
                        existingEntry.modified = today.valueOf();
                        await _updateTimeEntry(existingEntry, existingEntry.id);
                        // Clear local change tracking after successful save
                        delete props.localChangesRef.current[props.fieldName];
                    }
                }

                if (project.budget) {
                    console.debug("Project has budget: ", project.budget);
                    const body: IProjectResults = cloneDeep(project);
                    // delete body.company;

                    let _prev = prev;
                    if (typeof _prev === "string") _prev = parseFloat(_prev);
                    if (_prev === null || isNaN(_prev)) _prev = 0;

                    if (typeof current === "string") {
                        current = parseFloat(current);
                    }
                    if (_prev === null || isNaN(current)) current = 0;

                    body.budget.used = project.budget.used ? project.budget.used - (_prev - current) : current;

                    const ret: IProject = await updateProjectSettings(props.jwtoken, body, body.id);
                    const newObject = cloneDeep(props.projectsData); //make deep copy of original object

                    const index = newObject?.findIndex((x) => {
                        //find right object based on id
                        if (x.id === ret.id) return true;
                        else return false;
                    });

                    if (newObject && index !== -1 && index) {
                        newObject[index].budget = ret.budget; //update with new value
                    }

                    props.setProjectsData(newObject);
                }
            }
        } catch (error) {
            // setErrorMsg("Error updating time entry");
        } finally {
            props.setLoadingCells((prev: { [key: string]: boolean }) => ({
                ...prev,
                [cellKey]: false,
            }));
        }
    };

    return (
        <>
            {isLoading ? (
                <div className="flex justify-center items-center w-full min-w-20 h-full rounded">
                    <Spinner size="small" />
                </div>
            ) : (
                <div
                    className={`relative ${isLocked ? "opacity-90" : ""} ${holiday ? "bg-(--colorNeutralBackground2) rounded" : ""}`}
                    title={holiday ? holiday.name : undefined}
                >
                    {/* Manual mode indicator for Default Hours (subtle dot in corner).
                        Show the dot only on the project that is actually the effective
                        default for this specific day, to avoid duplicate indicators. */}

                    {/* Time entry input */}
                    <input
                        className={`text-center text-lg sm:text-base md:text-sm font-medium min-h-[48px] sm:min-h-[40px] md:min-h-[36px] h-12 sm:h-11 md:h-10 w-full max-w-24 rounded-md pl-2 sm:pl-1.5 md:pl-1.5 pr-8 sm:pr-[22px] md:pr-6 lg:pr-7 m-0 focus:outline-none focus:ring-2 focus:ring-fluent-blue-500 focus:border-fluent-blue-500 disabled:cursor-not-allowed placeholder:italic transition-colors duration-150 touch-manipulation ${
                            isLocked
                                ? "bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 font-semibold"
                                : "bg-(--colorNeutralBackground6) text-(--colorNeutralForeground1)"
                        } ${
                            isChanged
                                ? "ring-2 ring-cyan-400 ring-offset-1"
                                : isDefaultForDay && props.isManualDefaultDay
                                  ? "ring-2 ring-orange-400"
                                  : isDefaultForDay
                                    ? "ring-2 ring-purple-500"
                                    : ""
                        }`}
                        autoComplete="off"
                        disabled={isDisabled || isLoading}
                        type="text"
                        inputMode="decimal"
                        placeholder={isLocked && !isDefaultHoursProjectForDay ? "Locked" : props.placeholder}
                        id={id}
                        value={
                            isFocused
                                ? props.field[props.fieldName] || ""
                                : props.field[props.fieldName]
                                  ? formatValue(props.field[props.fieldName])
                                  : ""
                        }
                        onBlur={(e) => {
                            // Unregister cell as focused when user leaves the input
                            setIsFocused(false);
                            props.focusedCellsRef.current[cellKey] = false;

                            // Format the value before saving
                            const currentValue = e.target.value;
                            if (currentValue && currentValue !== "" && currentValue !== "0") {
                                const numValue = parseFloat(currentValue);
                                if (!isNaN(numValue) && numValue !== 0) {
                                    const formattedValue = numValue.toFixed(1);
                                    // Update field with formatted value
                                    const currentField = props.fieldRef.current || props.field;
                                    const updatedField = {
                                        ...currentField,
                                    };
                                    updatedField[props.fieldName] = formattedValue;
                                    props.setField(updatedField);
                                    // Update the input value
                                    e.target.value = formattedValue;
                                }
                            }

                            handleOnBlur(e.target, prev, props.projectID, props.date, props.project);
                        }}
                        onFocus={(e) => {
                            // Register cell as focused when user starts editing
                            setIsFocused(true);
                            props.focusedCellsRef.current[cellKey] = true;
                            setPrev(e.target.value);
                        }}
                        onChange={(e) => {
                            const data = { value: e.target.value };
                            const newValue = handleOnChange(data);
                            // Use fieldRef to get the latest field value (may have been updated by other cells)
                            const currentField = props.fieldRef.current || props.field;
                            const updatedField = { ...currentField };
                            updatedField[props.fieldName] = newValue;
                            props.setField(updatedField);
                            // Track this as a local unsaved change
                            props.localChangesRef.current[props.fieldName] = newValue;
                        }}
                    />
                    {/* Description dialog button */}
                    {id !== undefined && (
                        <div className="absolute right-1 sm:right-0.5 md:right-1 top-1/2 -translate-y-1/2 h-6 w-6 sm:h-5 sm:w-5 md:h-5 md:w-5 flex items-center justify-center touch-manipulation">
                            <DescriptionDialog
                                id={timeEntryID}
                                token={props.jwtoken}
                                hasDescription={hasDescription}
                                cellKey={cellKey}
                                submitReady={_handleDescriptionReady}
                                locked={isDisabled}
                                // Reset default hours only from the effective default project
                                // for this specific day, so there is a single reset action.
                                onResetDefaultHours={
                                    props.isDefaultForDay && timeEntryID
                                        ? async () => {
                                              try {
                                                  // Show loading state for this cell while resetting
                                                  props.setLoadingCells((prev) => ({
                                                      ...prev,
                                                      [cellKey]: true,
                                                  }));
                                                  const result = await resetDefaultHoursForTimeEntry(props.jwtoken, timeEntryID);
                                                  // Update default hours day state so manual/auto markers stay in sync
                                                  if (result?.day) {
                                                      props.onDefaultHoursDayUpdated?.(result.day);
                                                  }
                                                  // Refresh current week to reflect recalculated default hours
                                                  await props.fetchTimeEntries(props.selectedDays);
                                              } catch (error) {
                                                  console.error("Failed to reset default hours for entry", error);
                                              } finally {
                                                  props.setLoadingCells((prev) => ({
                                                      ...prev,
                                                      [cellKey]: false,
                                                  }));
                                              }
                                          }
                                        : undefined
                                }
                            />
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
